import { debounce } from 'lodash';
import { some } from 'lodash/fp';
import { array, string } from 'fp-ts';
import { createAction, DateTimeUtils, lt, showInfoAlert } from '@mews/framework';
import { CommanderThunkAction, WithScope, WithServiceId } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { StructuredDate } from '@optimus-web/core';
import { localizedDateStringToDate } from '@mews-commander/utils';
import { getVoucher } from '@mews-commander/legacy-entities/src/ducks/vouchers';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks';
import {
    getEndTime,
    getOrderValue,
    getServiceId,
    getStartDate,
    getStartTime,
    isReservationPlaceCity,
    getShowDateServiceDiscrepancyBanner,
} from '../selectors';
import { fetchRatePricing } from './api';
import { processSelectedReservationPlace } from './availability';
import { DATES_CHANGED, ORDER_VALUE_CHANGED, CATEGORY_OCCUPANCY_CHANGED, TIMES_CHANGED } from './types';
import { clearPricing } from './pricing';
import { clearDefaultRates } from './rateGroups';
import { preselectPartnerCompany, preselectTravelAgency } from './searchResult';
import { selectService } from './service';

export const changeDates =
    (startDate: StructuredDate, endDate: StructuredDate, scope: string): CommanderThunkAction =>
    async (dispatch, getState) => {
        // Drop the timezone because of chain mode. The timezoned date is assembled in api callls then.
        const startDateString = DateTimeUtils.structuredDateToISOString(startDate);
        const endDateString = DateTimeUtils.structuredDateToISOString(endDate);

        dispatch(createAction(DATES_CHANGED, { startDate: startDateString, endDate: endDateString }, { scope }));
        const state = getState();
        const serviceId = getServiceId(state, { scope });
        const timeUnit = getTimeUnitByServiceId(state, { serviceId });
        const startTime = getStartTime(state, { scope });
        const endTime = getEndTime(state, { scope });
        if (timeUnit !== TimeUnit.Hour || (timeUnit === TimeUnit.Hour && startTime && endTime)) {
            await dispatch(processSelectedReservationPlace(scope));
        }
        const showAlert = serviceId ? getShowDateServiceDiscrepancyBanner(state, { scope, serviceId }) : false;
        if (showAlert) {
            showInfoAlert({
                title: lt('SelectDifferentDepartureDate'),
                description: lt('DateServiceDiscrepancyWarningMessage'),
            });
        }
    };

export const changeTimes =
    ({
        startTime,
        endTime,
        isValidTimeRange,
        scope,
    }: {
        startTime: string;
        endTime: string;
        isValidTimeRange: boolean;
        scope: string;
    }): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const { appLocalizationComponent } = container();

        const startTimeString = isValidTimeRange ? appLocalizationComponent.parseShortTime(startTime).value : null;
        const endTimeString = isValidTimeRange ? appLocalizationComponent.parseShortTime(endTime).value : null;

        dispatch(createAction(TIMES_CHANGED, { startTime: startTimeString, endTime: endTimeString }, { scope }));
        const state = getState();
        const startDate = getStartDate(state, { scope });
        const endDate = getEndTime(state, { scope });
        if (startDate && endDate) {
            await dispatch(processSelectedReservationPlace(scope));
        }
    };

export const createChangeOrderValueAction = (key: $TSFixMe) => (value: $TSFixMe, scope: $TSFixMe) =>
    changeOrderValue({ key, value }, scope);

// prevent overfetching of rate price when changes to order options are fast enough
const dispatchDebouncedFetchRatePricing = debounce(
    (dispatch, scope) => dispatch(fetchRatePricing({ scope, origin: 'NRS-changeOrderValue' })),
    1000,
    {
        leading: true,
    }
);

export const changeOrderValue =
    ({ key, value }: { key: string; value: string | string[] }, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const currentOrderValue = getOrderValue(key)(getState(), { scope });
        const isEqual =
            Array.isArray(value) && Array.isArray(currentOrderValue)
                ? array.getEq(string.Eq).equals(value, currentOrderValue)
                : value === currentOrderValue;
        if (!isEqual) {
            dispatch(createAction(ORDER_VALUE_CHANGED, { key, value }, { scope }));
            dispatchDebouncedFetchRatePricing(dispatch, scope);
        }
    };

export const changeCategoryOccupancy = (occupancy: $TSFixMe, scope: $TSFixMe) => (dispatch: $TSFixMe) => {
    dispatch(createAction(CATEGORY_OCCUPANCY_CHANGED, occupancy, { scope }));

    const { occupancyData } = occupancy;
    const shouldFetchRatePricing = some(({ personCount }) => personCount > 0, occupancyData);

    if (shouldFetchRatePricing) {
        dispatch(fetchRatePricing({ scope, origin: 'NRS-changeCategoryOccupancy' }));
    }
};

export const changeDateOnSecondStep =
    ({ startDate, endDate, startTime, endTime, scope, serviceId }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState) => {
        const structuredStartDate = DateTimeUtils.dateToStructuredDate(localizedDateStringToDate(startDate));
        const structuredEndDate = DateTimeUtils.dateToStructuredDate(localizedDateStringToDate(endDate));
        dispatch(changeDates(structuredStartDate, structuredEndDate, scope));

        const state = getState();
        const isCityMode = isReservationPlaceCity(state, { scope });
        const timeUnit = getTimeUnitByServiceId(state, { serviceId });

        if (!isCityMode && timeUnit === TimeUnit.Hour) {
            dispatch(changeTimes({ startTime, endTime, isValidTimeRange: true, scope }));
        }
        dispatch(clearPricing(scope));
        dispatch(clearDefaultRates(scope));
        dispatch(fetchRatePricing({ scope, origin: 'NRS-changeDateOnSecondStep' }));
    };

export const changeServiceOnSecondStep =
    ({ serviceId, scope }: WithScope & WithServiceId): CommanderThunkAction =>
    async (dispatch) => {
        await dispatch(selectService(serviceId, scope));
        void dispatch(processSelectedReservationPlace(scope));
        dispatch(fetchRatePricing({ scope, origin: 'NRS-changeServiceOnSecondStep' }));
    };

const changeVoucher = createChangeOrderValueAction('voucherId');
export const changeVoucherValue =
    (value: string | null, serviceId: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        if (value === null) {
            return dispatch(changeVoucher(null, scope));
        }

        const voucher = getVoucher(getState(), { id: value });

        if (voucher?.companyId) {
            dispatch(preselectPartnerCompany({ id: voucher.companyId, scope }));
        }

        if (voucher?.travelAgencyId) {
            dispatch(preselectTravelAgency({ id: voucher.travelAgencyId, scope, serviceId }));
        }

        return dispatch(changeVoucher(value, scope));
    };
